// useMenuLevel.ts
import { useCallback } from "react";
import type { DropResult } from "@hello-pangea/dnd";
import type { MenuButton } from "@bale-bot/types";
import { asRows, replaceRow } from "./menuHelpers";

interface UseMenuLevelOptions {
  rows: MenuButton[][];
  onChange: (rows: MenuButton[][]) => void;
  depth: number;
}

export function useMenuLevel({ rows, onChange, depth }: UseMenuLevelOptions) {
  const isRoot = depth === 0;

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || result.destination.index === result.source.index) {
        return;
      }
      const newRows = [...rows];
      const [movedRow] = newRows.splice(result.source.index, 1);
      newRows.splice(result.destination.index, 0, movedRow);
      onChange(newRows);
    },
    [rows, onChange],
  );

  const handleAddRow = useCallback(() => {
    onChange([...rows, [{ text: "New Button", callback_data: "action" }]]);
  }, [rows, onChange]);

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      onChange(rows.filter((_, i) => i !== rowIndex));
    },
    [rows, onChange],
  );

  const handleAddButton = useCallback(
    (rowIndex: number) => {
      onChange(
        replaceRow(rows, rowIndex, [
          ...rows[rowIndex],
          { text: "New Button", callback_data: "action" },
        ]),
      );
    },
    [rows, onChange],
  );

  const handleDeleteButton = useCallback(
    (rowIndex: number, buttonIndex: number) => {
      const row = rows[rowIndex].filter((_, i) => i !== buttonIndex);
      onChange(
        row.length === 0
          ? rows.filter((_, i) => i !== rowIndex)
          : replaceRow(rows, rowIndex, row),
      );
    },
    [rows, onChange],
  );

  /** Immutable update of a single button – only the path that changed is recreated */
  const updateButton = useCallback(
    (
      rowIndex: number,
      buttonIndex: number,
      mutate: (button: MenuButton) => void,
    ) => {
      const button = { ...rows[rowIndex][buttonIndex] };
      mutate(button);

      onChange(
        replaceRow(
          rows,
          rowIndex,
          rows[rowIndex].map((current, i) =>
            i === buttonIndex ? button : current,
          ),
        ),
      );
    },
    [rows, onChange],
  );

  const handleUpdateButtonText = useCallback(
    (rowIndex: number, buttonIndex: number, text: string) => {
      updateButton(rowIndex, buttonIndex, (b) => {
        b.text = text;
      });
    },
    [updateButton],
  );

  const handleUpdateButtonAction = useCallback(
    (rowIndex: number, buttonIndex: number, value: string) => {
      updateButton(rowIndex, buttonIndex, (button) => {
        if (button.submenu !== undefined) {
          delete button.url;
          delete button.callback_data;
        } else if (value.trim().toLowerCase().startsWith("http")) {
          button.url = value;
          delete button.callback_data;
        } else {
          button.callback_data = value;
          delete button.url;
        }
      });
    },
    [updateButton],
  );

  const handleToggleSubmenu = useCallback(
    (rowIndex: number, buttonIndex: number) => {
      updateButton(rowIndex, buttonIndex, (button) => {
        if (button.submenu !== undefined) {
          delete button.submenu;
          delete button.id;
          button.callback_data = "action";
        } else {
          delete button.url;
          delete button.callback_data;
          button.id = button.id ?? crypto.randomUUID();
          button.submenu = [
            [{ text: "Submenu Item 1", callback_data: "sub_action" }],
          ];
        }
      });
    },
    [updateButton],
  );

  const handleSubmenuChange = useCallback(
    (rowIndex: number, buttonIndex: number, submenu: MenuButton[][]) => {
      updateButton(rowIndex, buttonIndex, (button) => {
        button.submenu = submenu;
      });
    },
    [updateButton],
  );

  // Convenience getters that the UI may need
  const getButtonMeta = useCallback(
    (button: MenuButton) => {
      const isFolder = button.submenu !== undefined;
      const actionValue = button.url ?? button.callback_data ?? "";
      const submenuRows = asRows(button.submenu);
      return { isFolder, actionValue, submenuRows };
    },
    [],
  );

  return {
    isRoot,
    handleDragEnd,
    handleAddRow,
    handleDeleteRow,
    handleAddButton,
    handleDeleteButton,
    handleUpdateButtonText,
    handleUpdateButtonAction,
    handleToggleSubmenu,
    handleSubmenuChange,
    getButtonMeta,
  };
}